<template>
  <div class="broadcast-box__container pos-rel" :style="{ width: _px(curSize), height: _px(curSize) }">
    <!-- sub별 상태 아이콘 (BroadcastBox 전용 자식 c/SubStatus). effectiveMode: auto(전부 ready면 hide) | always -->
    <SubStatus :statuses="displayStatuses" :details="statusDetailBySource" :mode="effectiveMode"></SubStatus>

    <!-- 레벨/가시성 오버레이 (with-outer일 때만) -->
    <OuterFacade v-if="withOuter" ref="outerFacade" :context="context"></OuterFacade>
    <!-- 3D 펫 본체 -->
    <ModelFacade
      ref="modelFacade"
      :context="context"
      :size="size"
      :preview-overlays="previewOverlays"
      :camera-control="cameraControl"
      @afterInit="$emit('afterInit', $event)"
    ></ModelFacade>
    <!-- 신원(broadcast_token) 확인 실패 → 로딩 위를 덮어 원인 표시 -->
    <AuthErrorLayer v-if="authError" :message="authError"></AuthErrorLayer>

    <!-- 뇌 구조 훔쳐보기: 제어판 트리거로 세션룰 키워드를 5초 오버레이(시청자 노출) -->
    <div v-if="brainVisible" class="brain-peek">
      <BrainWordCloud :words="sessionKeywords"></BrainWordCloud>
    </div>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";
import SignalingApi from "@/api/SignalingApi";
import SlackWebhook from "@/domain/slack/SlackWebhook";

import SleepCounter from "@/views/OnAir/-components/SleepCounter";
import levelExpMixin from "@/views/OnAir/-mixins/levelExpDataMixin";
import { chatHandler, donationHandler } from "@/views/OnAir/socketHandlers";
import {
  StreamRegistry,
  ChzzkDirectSource,
  TwitchDirectSource,
  VirtupetGatewaySource,
  ChzzkGatewaySource,
} from "@/domain/stream";

import SubStatus from "./c/SubStatus.vue";
import AuthErrorLayer from "@/components-model/layer/AuthErrorLayer.vue";
import { idToModelUrl } from "@/constants";
import BrainWordCloud from "@/views/Lab/BrainWordCloud.vue";
import { rulesToWeightedWords } from "@/domain/session/sessionKeywords";
import { resolveConnStatusMode } from "@/schema/FullSchema";

// 저장/소켓 값("always"|"error"|"off"|레거시 boolean) → SubStatus mode
const toStatusMode = (v) => {
  const m = resolveConnStatusMode(v);
  return m === "off" ? "hidden" : m;
};

const BRAIN_PEEK_MS = 5000; // 뇌 구조 훔쳐보기 노출 시간

// 펫 모델 + (선택) 이벤트 스트림을 소유하는 자족형 컴포넌트 (PetAggregate Body+Brain).
//  - sources : 구독할 provider 목록(['virtupet','chzzk','twitch']). []면 소켓 없음(정적 — 외부 메서드로 구동).
//  - token   : sub 자격증명(anon overlay=URL토큰 / 제어판=자기 펫 토큰). 서버가 petId/ownerId 해석.
//  - pub 없음(sub 전용). mode(direct/relay)는 provider에서 파생(chzzk/virtupet=relay, twitch=direct).
//  데이터: pet_id→owner_id를 서버가 토큰에서 해석 → owner/pet 공개 RPC로 instruction/rule/level/model 로드.
//
//  [구동 2경로 — 둘 다 결국 ModelFacade/OuterFacade로 수렴]
//   1) sub 스트림(소켓): sources+token → 이벤트 → _onStreamEvent 인터프리터 → 내부에서 아래 메서드 호출(자동).
//   2) 공개 메서드($refs, 소켓 불필요): 부모가 $refs.broadcastBox.X() 직접 호출.
//      init/initContext/initSetting/initOuterVisibility/setReactions/changeModelReverse/
//      changeBackgroundCanvas/startIdle/executeInstruction/stopInstruction/executeCmds/delegate/
//      updateVisibility/showSync/displayExp/listAnimations/restoreCamera/inject(디버그)
//   → 즉 sub는 #2를 자동 호출하는 피더. 정적 페이지(sources=[])는 #2만, 제어판은 #1 미러+#2 로컬, 오버레이는 주로 #1.
export default {
  components: { SubStatus, AuthErrorLayer, BrainWordCloud },
  mixins: [levelExpMixin],
  props: {
    sources: { type: Array, default: () => [] },
    token: { type: String, default: null },
    withOuter: { type: Boolean, default: false },
    size: { type: [Number, String], default: 400 },
    // true면 상태바 항상 표시 + 미설정 채팅 후보를 "설정안됨"으로 노출(디버그/dev 페이지용).
    //  (송출 화면은 false → broadcastSetting의 statusMode(always/hidden)만 따름)
    alwaysShowStatus: { type: Boolean, default: false },
    // 편집기 정적 미리보기 오버레이(재생헤드 시점) → ModelFacade 밴드로 전달
    previewOverlays: { type: Array, default: () => [] },
    // 마우스 카메라 조작(OrbitControls) on/off. false면 이벤트 통과(카메라 고정)
    cameraControl: { type: Boolean, default: true },
  },
  data() {
    return {
      context: { streamerAliasId: "**", virtupetProfile: {} },
      statusBySource: {}, // 실제 인스턴스화한 sub만 추적: { [provider]: connecting|ready|failed|closed }
      statusDetailBySource: {}, // failed 원인 코드 { [provider]: E_5XX|E_NETWORK|E_SOCKET|HTTP_xxx } — 상태바 병기
      // sub 상태바 3모드: "always"(항상) | "error"(에러시에만·기본) | "hidden"(꺼짐).
      //  broadcastSetting outerFacadeVisibility['connection-status'] 값(resolveConnStatusMode)으로 결정.
      statusMode: "error",
      authError: null, // 신원 확인 실패 메시지(있으면 오버레이에 표시)
      broadcasting: false, // 방송(이벤트 처리) 게이트. OBS streaming 여부(비OBS=항상 true). reactive(상태바 처리중 표시용)
      sessionKeywords: [], // 세션룰 키워드({word,weight}) — 뇌 구조 훔쳐보기용(setSessionRules에서 갱신)
      brainVisible: false, // 뇌 구조 훔쳐보기 오버레이 표시 여부(제어 op로 5초 토글)
      curSize: this.size, // 런타임 리사이즈용(상주 모델 페이지별 크기)
    };
  },
  computed: {
    // 상태바 표시 모드. alwaysShowStatus(dev)면 broadcastSetting과 무관하게 강제 always.
    effectiveMode() {
      return this.alwaysShowStatus ? "always" : this.statusMode;
    },
    // 표시용 상태 = 순수 소켓 상태 + 처리 게이트 조합. ready인데 처리중이면 "processing".
    //  virtupet: ready면 항상 처리중(제어는 방송 무관). chzzk: ready + 방송중일 때만 처리중.
    displayStatuses() {
      const out = {};
      Object.keys(this.statusBySource).forEach((provider) => {
        const st = this.statusBySource[provider];
        if (st === "ready") {
          const processing = provider === "virtupet" ? true : this.broadcasting;
          out[provider] = processing ? "processing" : "ready";
        } else {
          out[provider] = st;
        }
      });
      return out;
    },
  },
  mounted() {
    if (this.sources.length) this._setupStream();
  },
  destroyed() {
    this._destroyed = true; // 비동기(_setupStream/_onAuthed) 진행 중 언마운트 가드용
    if (this._registry) this._registry.closeAll();
    if (this._sleepCounter) this._sleepCounter.stop();
    if (this.clearPersistInterval) this.clearPersistInterval();
    if (this._onObsStart) window.removeEventListener("obsStreamingStarted", this._onObsStart);
    if (this._onObsStop) window.removeEventListener("obsStreamingStopped", this._onObsStop);
    if (this._brainTimer) clearTimeout(this._brainTimer);
  },
  methods: {
    // ───────── 스트림(sub) 셋업 ─────────
    //  신원·모델·직결채팅 = Edge(게이트웨이 무관). 제어(V-INSTRUCTION) = 게이트웨이 relay 전용(다운돼도 펫은 뜸).
    async _setupStream() {
      const registry = new StreamRegistry();
      this._registry = registry;
      // 모든 sub는 event-stream → 단일 인터프리터가 type으로 해석
      registry.route("event-stream", (event, provider) => this._onStreamEvent(event, provider));

      if (!this.sources.includes("virtupet")) return;

      // 1) 신원 = Edge signaling. 게이트웨이 없이 모델/데이터/직결채팅까지 진행.
      let identity;
      try {
        identity = await SignalingApi.identity(this.token);
      } catch (error) {
        // 실패 원인 표시(토큰 만료/무효 vs 일시 오류) → 오버레이에 직접 노출(로딩 멈춤 방지)
        this.authError =
          error.status === 401 || error.status === 400
            ? "OBS 링크가 만료되었거나 유효하지 않습니다. 제어판에서 링크를 다시 발급해주세요."
            : "신원 확인 중 오류가 발생했습니다. 네트워크를 확인하고 새로고침해주세요.";
        return;
      }
      if (this._destroyed) return; // identity 대기 중 언마운트되면 중단
      await this._onAuthed(identity.email, identity.petId, identity.ownerId, identity.name, identity.platforms);

      // 2) 제어(V-INSTRUCTION) sub = 게이트웨이 relay. 실패해도 펫/직결채팅은 이미 동작(제어판 원격제어만 비활성).
      const control = new VirtupetGatewaySource({ token: this.token });
      this.$set(this.statusBySource, "virtupet", control.status); // idle → open()이 connecting→ready로 구동
      control.onStatusChange((st, detail) => {
        this.$set(this.statusBySource, "virtupet", st);
        this.$set(this.statusDetailBySource, "virtupet", detail || null);
      });
      this._controlSource = registry.add(control);
      control.open();
    },

    // 신원 확보(1회) → owner/pet 데이터 로드 + 모델/룰/레벨 셋업 + chat 소스 구성
    //  name = user_account.name(표시명), platforms = identity.platforms(연동/활성/채널)
    async _onAuthed(email, petId, ownerId, name, platforms) {
      if (this._authed) return;
      this._authed = true;

      this.email = email;
      this.petId = petId; // 공개 RPC 키
      this.ownerId = ownerId;
      SupabaseApi.setPetId(petId);
      SlackWebhook.setUser(email);

      const pet = (await SupabaseApi.PetProfile.fetchPublicPet(petId)) || {};

      // 리액션 해석 맵(공개 RPC: system + 이 pet 소유자의 user 리액션). id(rct_)·slug 모두 해석 가능.
      const reactions = await SupabaseApi.Reaction.fetchPublic(petId);
      // 비동기 대기 중 언마운트/재생성 시 $refs.modelFacade가 사라짐 → 가드 후 중단
      if (this._destroyed || !this.$refs.modelFacade) return;
      const reactionMaps = SupabaseApi.Reaction.toMaps(reactions);
      this.$refs.modelFacade.setReactions(reactionMaps);
      // 매처가 쓰는 해석 맵(구체 매칭 dangling 필터 + fallback slug→id)
      this._reactionById = reactionMaps.byId;
      this._reactionBySlug = reactionMaps.bySlug;

      await this.$refs.modelFacade.init(idToModelUrl(pet.model_url)); // 저장은 id → 재생 직전 model_url 해석
      await this._initOuterVisibility(petId);

      // 펫 시각설정(배경/반전)은 pet_broadcast_setting, 룰은 owner 테이블(공개 RPC).
      const broadcastSetting = (await SupabaseApi.PetBroadcastSetting.fetchPublicBroadcastSetting(petId)) || {};
      const { isReverseModel, backgroundSetting, effectSetting, isMuted } = broadcastSetting;
      // 배경 통합 설정 { file_name, visible } — 파일명은 토글 재생용으로 보관, visible로 최초 표시 게이트
      const bg = backgroundSetting || {};
      this._backgroundFilename = bg.file_name || null;
      this._backgroundVisible = bg.visible !== false;
      // 표시명 = user_account.name (구 streamer_alias_id). 인트로/식별 라벨에 사용.
      this.context.streamerAliasId = name || "";

      const ruleSet = await SupabaseApi.BroadcastRules.fetchPublic(petId);
      this._updateRuleSet(ruleSet);

      if (this._destroyed || !this.$refs.modelFacade) return;
      this.$refs.modelFacade.changeModelReverse(isReverseModel || false);
      this.$refs.modelFacade.changeBackgroundCanvas(this._backgroundVisible ? this._backgroundFilename : null);
      // 파워 이펙트 콜드스타트 — { enabled, style } (모델 뒤 파티클 레이어)
      const fx = effectSetting || {};
      this.$refs.modelFacade.setEffect(fx.enabled, fx.style || "particles");
      // 오디오 뮤트 콜드스타트
      this.$refs.modelFacade.setMuted(!!isMuted);
      // 레벨·경험치 통합 설정 { visible, color } — 가시성은 exp-bar-layer 토글, 색은 ExpBar에 적용
      const exp = broadcastSetting.expSetting || {};
      this._expColor = exp.color || "#9155fd";
      if (this.$refs.outerFacade) {
        this.$refs.outerFacade.updateVisibility("outerFacade#exp-bar-layer", exp.visible !== false);
        this.$refs.outerFacade.setExpColor(this._expColor);
      }
      this.context.virtupetProfile = pet;
      // 호스트 페이지용 신원 알림 (예: /on-air 브라우저 탭 제목)
      this.$emit("identity", { streamerName: name || "", petName: pet.name || "" });

      await this.startLevelExp(); // 레벨/경험치 (mixin)
      this._startIdleAndSleep();

      this._setupChatSource(platforms || {});
    },

    // chat sub: identity.platforms 기반 다중 소스(브라우저 직결). isEnabled && isConnected 인 것만 오픈.
    //  - twitch: 익명 IRC 직결 (채널명 필요 — identity가 channelName 제공)
    //  - chzzk : signaling으로 세션 발급/구독만 위임, 스트림 직결 (토큰 기반, 채널명 불요)
    //  소켓은 방송과 무관하게 즉시 연결(상태=순수 소켓). 방송 여부는 처리 게이트(broadcasting)로만.
    _setupChatSource(platforms = {}) {
      this._chatSources = [];

      const open = (provider, source) => {
        this._chatSources.push(this._registry.add(source));
        this.$set(this.statusBySource, provider, source.status); // idle
        source.onStatusChange((st, detail) => {
          this.$set(this.statusDetailBySource, provider, detail || null);
          // 미연동(unconfigured)은 송출 화면에선 숨기고, dev(alwaysShowStatus)에선 "설정안됨"으로 유지.
          if (st === "unconfigured" && !this.alwaysShowStatus) this.$delete(this.statusBySource, provider);
          else this.$set(this.statusBySource, provider, st);
        });
        source.open();
      };

      // 채팅 후보별 (설정 충족 여부 + 소스 팩토리). 충족 시 open, 미충족이면 dev에서만 "설정안됨" 노출.
      //  - twitch: 익명 IRC 직결(채널명 필요) / chzzk: 본인 OAuth direct / chzzk_relay: 관리자 relay(게이트웨이 Kimcore)
      const tw = platforms.twitch || {};
      const ch = platforms.chzzk || {};
      const rel = platforms.chzzk_relay || {};
      // provider별 채널이름 — 채팅 로그 네임스페이스(owner/채널/platform)용.
      // chzzk_relay=relay 대상 채널, twitch/chzzk=본인 채널명(없으면 null→unknown).
      this._channelByProvider = {
        twitch: tw.channelName || null,
        chzzk: ch.channelName || null,
        chzzk_relay: rel.channelName || null,
      };
      const candidates = [
        {
          provider: "twitch",
          ok: !!(tw.isEnabled && tw.isConnected && tw.channelName),
          make: () => new TwitchDirectSource({ channel: tw.channelName }),
        },
        {
          provider: "chzzk",
          ok: !!(ch.isEnabled && ch.isConnected),
          make: () => new ChzzkDirectSource({ token: this.token }),
        },
        {
          provider: "chzzk_relay",
          ok: !!(rel.isConnected && rel.channelName),
          make: () => new ChzzkGatewaySource({ token: this.token, streamerAliasId: rel.channelName, relay: true }),
        },
      ];
      candidates.forEach(({ provider, ok, make }) => {
        if (!this.sources.includes(provider)) return;
        if (ok) open(provider, make());
        else if (this.alwaysShowStatus) this.$set(this.statusBySource, provider, "unconfigured"); // dev: 설정안됨 노출
      });

      // 세션 신원(owner/주채널/플랫폼)을 상위로 — 첫 채팅 기다리지 않고 마운트 시 캐시 워밍 스타트용.
      const primary = candidates.find(
        (c) => c.ok && this.sources.includes(c.provider) && this._channelByProvider[c.provider],
      );
      this.$emit("authed", {
        ownerId: this.ownerId,
        petId: this.petId,
        platform: primary ? primary.provider : null,
        channel: primary ? this._channelByProvider[primary.provider] : null,
      });

      // 처리 게이트(broadcasting)는 "이 오버레이가 실제 방송 중인가(OBS streaming)"를 뜻하며
      // 채팅 소스(치지직/트위치) 연동 여부와 무관. 항상 초기화해야 virtupet relay(테스트 채팅)도
      // 미리보기(비OBS=항상 처리)에서 정상 반응한다. (조건부였을 때 미연동 시 CHAT 전부 버려지던 버그)
      this._setupBroadcastGating();
    },

    // 방송(OBS streaming) 여부 = "이벤트 처리" 게이트만 토글. 소켓 open/close는 하지 않음(연결 상태는 소켓 그대로).
    _setupBroadcastGating() {
      // [임시] start streaming 게이트 해제.
      //  WHY: 원래는 OBS에서 실제 "Start Streaming"(window.obsstudio.getStatus().streaming) 중일 때만
      //   채팅/도네이션을 처리했다. 그런데 송출 전 세팅·테스트 단계에선 broadcasting=false라 viewer 이벤트가
      //   전량 드롭돼(피드엔 뜨는데 펫은 무반응) 반응 확인이 안 되는 불편이 있었다. → 송출 여부와 무관하게
      //   항상 처리하도록 임시로 오픈. 부작용: 실제 방송이 아닐 때도 펫이 채팅에 반응함.
      //  되돌리기: 이 줄 삭제 + 아래 주석 블록 복구 + _onObsStop의 broadcasting=false 복구.
      this.broadcasting = true;
      // if (window.obsstudio) {
      //   this.broadcasting = false; // OBS면 streaming 확인 전까지 처리 안 함
      //   window.obsstudio.getStatus((status) => {
      //     this.broadcasting = !!status.streaming;
      //   });
      // } else {
      //   this.broadcasting = true; // OBS 아님(미리보기/dev) → 항상 처리
      // }
      this._onObsStart = async () => {
        this.broadcasting = true;
        await SlackWebhook.sendKey((this.context.virtupetProfile && this.context.virtupetProfile.name) || "");
      };
      this._onObsStop = () => {
        // [임시] 위 게이트 해제와 짝. WHY: 송출 종료 이벤트로 broadcasting=false를 되돌리면 다시 채팅이
        //  막히므로, "항상 처리" 상태를 유지하려고 무력화해 둠. 되돌리려면 아래 줄 주석 해제.
        // this.broadcasting = false;
      };
      window.addEventListener("obsStreamingStarted", this._onObsStart);
      window.addEventListener("obsStreamingStopped", this._onObsStop);
    },

    // ───────── 단일 인터프리터 + Reaction ─────────
    _onStreamEvent(event = {}, provider = null) {
      if (event.method) return this._handleControlOp(event); // 제어 op(updateRules/visibility)는 항상
      // 방송 전(처리 게이트 off)엔 시청자 이벤트 무시. V-INSTRUCTION(스트리머 제어)은 항상 실행.
      const isViewerEvent = event.type === "CHAT" || event.type === "DONATION" || event.type === "SUBSCRIPTION";
      // 시청자 이벤트는 처리 게이트와 무관하게 피드로 올림(모니터용). 펫 반응은 아래 게이트가 제어.
      if (isViewerEvent) {
        const evProvider = provider || event.provider || "unknown";
        this.$emit("stream-event", {
          provider: evProvider,
          type: event.type,
          nickname: (event.profile && event.profile.nickname) || null,
          message: event.message || "",
          // 키워드룰(활성) 매칭 단어들 — dev 채팅 피드 하이라이트용
          matchedKeywords: event.type === "CHAT" ? this._matchedKeywordsFor(event.message) : [],
          amount: (event.extras && event.extras.payAmount) || null,
          ownerId: this.ownerId, // rnd /chat-log 적재용(토큰 대신 클라가 신원 직접 전달)
          petId: this.petId,
          channelName: (this._channelByProvider || {})[evProvider] || null, // 네임스페이스용 채널명
          raw: event, // 원본(정규화된) 이벤트 — 미추출 정보 보존
        });
      }
      if (document.visibilityState === "hidden") return;
      if (isViewerEvent && !this.broadcasting) return;
      switch (event.type) {
        case "CHAT":
          chatHandler.call(this, { ...this.context, chat: event });
          break;
        case "DONATION":
          donationHandler.call(this, { ...this.context, donation: event });
          break;
        case "V-INSTRUCTION":
          this.executeInstruction(event.actionId, event.context, event.userInput);
          break;
        // SUBSCRIPTION: 추후
      }
    },
    // 잔여 제어 op(updateRules/updateVisibility) — 펫 행동 아닌 오버레이 config/UI
    _handleControlOp(data = {}) {
      const { method } = data;
      if (method === "updateRules") {
        const { time } = data;
        const canUpdate = !this._lastUpdatedTime || this._lastUpdatedTime < time;
        if (!canUpdate) return;
        this._lastUpdatedTime = time;
        this._updateRuleSet(data.body);
      } else if (method === "updateVisibility") {
        if (!data.body) return;
        const { targetEl, isVisible, userInput } = data.body;
        this.updateVisibility(targetEl, isVisible, userInput);
      } else if (method === "updateBackground") {
        // 배경 파일 라이브 교체(+가시성). 저장 파일명 갱신 후 즉시 반영.
        if (!data.body) return;
        const { fileName, isVisible } = data.body;
        this._backgroundFilename = fileName || null;
        this._backgroundVisible = isVisible !== false;
        this.changeBackgroundCanvas(this._backgroundVisible ? this._backgroundFilename : null);
      } else if (method === "updateModelReverse") {
        // 모델 좌우반전 라이브 적용
        if (!data.body) return;
        this.changeModelReverse(!!data.body.isReverse);
      } else if (method === "updateExpColor") {
        // 레벨·경험치 바 색 라이브 적용
        if (!data.body) return;
        this._expColor = data.body.color || "#9155fd";
        if (this.$refs.outerFacade) this.$refs.outerFacade.setExpColor(this._expColor);
      } else if (method === "updateEffect") {
        // 파워 이펙트 on/off + 스타일 라이브 적용
        if (!data.body) return;
        const { enabled, style } = data.body;
        if (this.$refs.modelFacade) this.$refs.modelFacade.setEffect(enabled, style);
      } else if (method === "updateMute") {
        // 오디오 뮤트 라이브 적용
        if (!data.body) return;
        if (this.$refs.modelFacade) this.$refs.modelFacade.setMuted(!!data.body.muted);
      }
      this.showSync();
    },
    // 채팅 매처("ㅋ")가 호출 — 모델 뒤 파워 이펙트 1회 발동 (비활성이면 modelFacade가 무시)
    triggerEffect() {
      if (this.$refs.modelFacade) this.$refs.modelFacade.burstEffect();
    },
    // 메시지에 포함된 활성 키워드룰 단어 목록(중복 제거). dev 채팅 피드 하이라이트용.
    _matchedKeywordsFor(message) {
      const msg = (message || "").toLowerCase();
      if (!msg) return [];
      const rules = [...(this._keywordRules || []), ...(this._sessionRules || [])].filter((r) => r.isEnabled);
      const set = new Set();
      for (const r of rules) {
        for (const k of r.keywords || []) {
          if (k && msg.includes(k.toLowerCase())) set.add(k);
        }
      }
      return [...set];
    },
    _updateRuleSet(ruleSet = {}) {
      const { keywordRules = [], donationRules = [], donationEnabled = true } = ruleSet;
      this._keywordRules = keywordRules;
      this._donationRules = donationRules;
      this._donationEnabled = donationEnabled;
    },

    // SESSION 키워드 룰(rnd 생성분) 주입 — DB 계층(_keywordRules)과 별개인 휘발 레이어.
    // 매처가 [...keywordRules, ...sessionRules]로 union하므로 여기만 갈아끼우면 됨. 세션 끝나면 소멸.
    setSessionRules(serverRules = []) {
      this._sessionRules = (serverRules || []).map((r, i) => ({
        id: `session-${i}`,
        isMentionMode: false, // SESSION은 일반 매칭만
        keywords: r.keywords || [],
        reactionId: r.reaction_id,
        isEnabled: true,
        label: r.reaction_label || "",
        layer: "SESSION",
      }));
      // 뇌 구조 훔쳐보기용 키워드({word,weight}) — 매처와 같은 소스에서 함께 갱신
      this.sessionKeywords = rulesToWeightedWords(serverRules);
    },

    // 제어판 "뇌 구조 훔쳐보기" → 세션룰 워드클라우드를 5초 오버레이 후 자동 숨김
    _showBrainPeek() {
      this.brainVisible = true;
      if (this._brainTimer) clearTimeout(this._brainTimer);
      this._brainTimer = setTimeout(() => {
        this.brainVisible = false;
      }, BRAIN_PEEK_MS);
    },

    async _initOuterVisibility(petId) {
      const { outerFacadeVisibility } =
        (await SupabaseApi.PetBroadcastSetting.fetchPublicBroadcastSetting(petId)) || {};
      if (outerFacadeVisibility) this.initOuterVisibility(outerFacadeVisibility); // outer + connection-status(statusMode)
      return outerFacadeVisibility;
    },

    async _startIdleAndSleep() {
      this.$refs.modelFacade.startIdle("action-only--{{loop}}idle-40");
      const idleInstructionIds = [
        "legacy-sleep",
        "Angry Gesture",
        "Happy",
        "Happy Idle",
        "Head Nod Yes",
        "Standing W_Briefcase Idle",
        "Zombie Idle",
        "Thoughtful Head Nod",
        "Breathing Idle",
      ].map((str) => "action-only--" + str.toLowerCase());
      try {
        this._sleepCounter = new SleepCounter();
        this._sleepCounter.on(() => {
          const instructionId = idleInstructionIds[Math.floor(Math.random() * idleInstructionIds.length)];
          this.$refs.modelFacade.executeInstruction(instructionId);
        });
        this._sleepCounter.start();
      } catch (error) {
        console.error(error);
      }
    },

    // ───────── 공개 인터페이스 (정적/제어판 직접 구동 + reaction 내부 호출) ─────────
    init(model, cameraPosition) {
      return this.$refs.modelFacade.init(model, cameraPosition);
    },
    deinit() {
      return this.$refs.modelFacade.deinit();
    },
    initContext(context) {
      this.context = context;
    },
    initSetting(fullSettingSchema) {
      const { backgroundFilename = null, isReverseModel = false } = fullSettingSchema;
      this.initModelFacadeSetting({ backgroundFilename, isReverseModel });
      this.initOuterVisibility(fullSettingSchema);
    },
    initModelFacadeSetting(modelFacadeSetting) {
      const { isReverseModel, backgroundFilename } = modelFacadeSetting;
      this.changeModelReverse(isReverseModel);
      this.changeBackgroundCanvas(backgroundFilename);
    },
    initOuterVisibility(visibility) {
      if (this.$refs.outerFacade) this.$refs.outerFacade.initVisibility(visibility);
      // connection-status는 OuterFacade 요소가 아니라 sub 상태바 모드로 해석
      if (visibility && Object.prototype.hasOwnProperty.call(visibility, "connection-status")) {
        this.statusMode = toStatusMode(visibility["connection-status"]);
      }
    },
    setReactions(maps) {
      this.$refs.modelFacade.setReactions(maps);
    },
    changeModelReverse(isReverseModel) {
      this.$refs.modelFacade.changeModelReverse(isReverseModel || false);
    },
    // 런타임 리사이즈(상주 모델): 컨테이너 + ModelFacade(렌더러) 함께 갱신
    resize(size) {
      this.curSize = size;
      if (this.$refs.modelFacade) this.$refs.modelFacade.resize(size);
    },
    isInitialized() {
      return !!(this.$refs.modelFacade && this.$refs.modelFacade.isInitialized);
    },
    changeBackgroundCanvas(backgroundFilename) {
      this.$refs.modelFacade.changeBackgroundCanvas(backgroundFilename);
    },
    startIdle(instructionId) {
      this.$refs.modelFacade.startIdle(instructionId);
    },
    executeInstruction(instructionId, context = {}, userInput) {
      this.$refs.modelFacade.executeInstruction(instructionId, context, userInput);
    },
    stopInstruction() {
      this.$refs.modelFacade.stopInstruction();
    },
    pauseInstruction() {
      this.$refs.modelFacade.pauseInstruction();
    },
    resetToIdle() {
      this.$refs.modelFacade.resetToIdle();
    },
    executeCmds(cmds, context, userInput) {
      // context 전달 필수 — 자막 이름표(펫 메타)·{{...}} 텍스트 치환이 여기서 끊기면 재생 시 누락됨
      this.$refs.modelFacade.executeCmds(cmds, context, userInput);
    },
    delegate(method, targetRef) {
      this.$refs.modelFacade[method](targetRef);
    },
    updateVisibility(targetEl, isVisible, userInput) {
      // connection-status는 OuterFacade 요소가 아니라 sub 상태바 모드 토글
      const [, id] = String(targetEl).split("#");
      if (id === "connection-status") {
        this.statusMode = toStatusMode(isVisible);
        return;
      }
      // brain-cloud는 span 오버레이가 아니라 컴포넌트 → 여기서 가로채 5초 피크
      if (id === "brain-cloud") {
        if (isVisible) this._showBrainPeek();
        else this.brainVisible = false;
        return;
      }
      // background-canvas는 outerFacade 요소가 아니라 배경 lottie → 저장된 파일명으로 껐다/켰다
      if (id === "background-canvas") {
        this._backgroundVisible = isVisible;
        this.changeBackgroundCanvas(isVisible ? this._backgroundFilename : null);
        return;
      }
      this.$refs.modelFacade.updateVisibility(targetEl, isVisible, userInput);
      if (this.$refs.outerFacade) this.$refs.outerFacade.updateVisibility(targetEl, isVisible, userInput);
    },
    showSync() {
      if (this.$refs.outerFacade) this.$refs.outerFacade.showSync();
    },
    displayExp(level, nextExp, percent) {
      if (this.$refs.outerFacade) this.$refs.outerFacade.displayExp(level, nextExp, percent);
    },
    listAnimations() {
      return this.$refs.modelFacade._appRenderer.appAnimationController.meta;
    },
    // 인생네컷: 포즈 캡처 패스스루 → PNG dataURL
    capturePose(animationId, t01) {
      return this.$refs.modelFacade.capturePose(animationId, t01);
    },
    restoreCamera() {
      this.$refs.modelFacade._appRenderer.restoreCamera();
    },

    // 디버그 주입 (DebugPanel): StreamEvent를 인터프리터에 직접 흘림 (실스트림 없이 반응 테스트)
    inject(event) {
      this._onStreamEvent(event, event.provider || "debug");
    },

    _px(value) {
      if (typeof value === "number") return `${value}px`;
      return value.indexOf("px") === -1 ? `${value}px` : value;
    },
  },
};
</script>

<style scoped>
/* 뇌 구조 훔쳐보기 오버레이 — 컨테이너 전체를 덮고 어둡게 + 브레인 중앙 */
.brain-peek {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 16, 26, 0.82);
}
.brain-peek >>> .brain-chart {
  width: 100%;
  height: 100%;
  max-width: none;
}
</style>
